import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Check, ArrowUp, ArrowDown, Zap, CalendarClock, ShieldAlert, RotateCcw, Sparkles } from "lucide-react";

const Billing = () => {
  const { user, refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // plan id being changed / "cancel" / "resume"

  const load = () =>
    api.get("/subscription/info").then((r) => setInfo(r.data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const active = info?.status === "active";
  const pendingCancel = info?.cancel_at_period_end;
  const periodEnd = info?.current_period_end ? new Date(info.current_period_end) : null;
  const fmtDate = (d) => d?.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  const changePlan = async (plan) => {
    setBusy(plan.id);
    try {
      await api.post("/subscription/change", { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      await Promise.all([refreshUser(), load()]);
      toast.success(`You're now on the ${plan.name} plan.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change plan");
    } finally {
      setBusy(null);
    }
  };

  const subscribe = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout unavailable");
      setBusy(null);
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/subscription/cancel");
      await Promise.all([refreshUser(), load()]);
      toast.success("Cancellation scheduled. Check your inbox for a little something 💙");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel");
    } finally {
      setBusy(null);
    }
  };

  const resume = async () => {
    setBusy("resume");
    try {
      await api.post("/subscription/resume");
      await Promise.all([refreshUser(), load()]);
      toast.success("Welcome back! Your subscription will continue.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not resume");
    } finally {
      setBusy(null);
    }
  };

  const currentPrice = info?.price;
  const cta = (p) => {
    if (info?.plan_id === p.id && active) return "current";
    if (active && currentPrice != null) return p.price > currentPrice ? "upgrade" : p.price < currentPrice ? "downgrade" : "current";
    return "get";
  };

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-black tracking-tight">Billing</h1>
          <p className="text-muted-foreground mt-2">Manage your plan — upgrade, downgrade, or cancel anytime. No emails required.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : (
          <>
            {/* Current subscription summary */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8 mb-8" data-testid="billing-summary">
              {active ? (
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-primary font-bold mb-1">Current plan</div>
                    <div className="font-heading text-3xl font-black" data-testid="billing-plan-name">
                      {info.plan_name} · ${info.price}/{info.interval === "year" ? "yr" : "mo"}
                    </div>
                    {info.comped ? (
                      <div className="text-sm text-primary mt-2 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Complimentary access granted by our team</div>
                    ) : pendingCancel ? (
                      <div className="text-sm text-amber-400 mt-2 flex items-center gap-1.5" data-testid="billing-cancel-notice">
                        <CalendarClock className="w-4 h-4" /> Cancels on {fmtDate(periodEnd) || "period end"} — you keep access until then.
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
                        <CalendarClock className="w-4 h-4" /> Renews {periodEnd ? `on ${fmtDate(periodEnd)}` : "automatically"}.
                      </div>
                    )}
                  </div>
                  <div>
                    {pendingCancel ? (
                      <Button onClick={resume} disabled={busy === "resume"} data-testid="resume-sub-btn"
                        className="rounded-full bg-primary text-background font-bold hover:bg-primary/90">
                        {busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-1.5" /> Keep my plan</>}
                      </Button>
                    ) : !info.comped && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="rounded-full border-white/15 text-foreground" data-testid="cancel-sub-btn" disabled={busy === "cancel"}>
                            {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel subscription"}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-secondary" /> Cancel your subscription?</AlertDialogTitle>
                            <AlertDialogDescription>
                              You'll keep full access until {fmtDate(periodEnd) || "the end of your paid period"}, then your campaign pauses.
                              We'll also email you a special offer in case you change your mind.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-full">Keep plan</AlertDialogCancel>
                            <AlertDialogAction onClick={cancel} className="rounded-full bg-secondary text-white hover:bg-secondary/90" data-testid="confirm-cancel-btn">
                              Yes, cancel at period end
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-xs uppercase tracking-widest text-primary font-bold mb-1">No active plan</div>
                  <div className="font-heading text-2xl font-black">Pick a plan to launch your growth campaign</div>
                  <div className="text-sm text-muted-foreground mt-1">Choose one below to get started in minutes.</div>
                </div>
              )}
            </motion.div>

            {/* Plan options */}
            <div className="grid md:grid-cols-3 gap-6 items-start">
              {(info?.plans || []).map((p, i) => {
                const state = cta(p);
                const featured = p.id === "growth";
                return (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                    className={`rounded-2xl p-7 border ${featured ? "border-primary/40 bg-card shadow-[0_0_30px_rgba(0,242,254,0.12)]" : "border-border/50 bg-card"}`} data-testid={`billing-plan-${p.id}`}>
                    {state === "current" && <div className="inline-flex mb-3"><span className="px-3 py-1 rounded-full bg-primary/15 text-primary text-xs font-bold uppercase tracking-wider">Your plan</span></div>}
                    <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{p.tagline}</p>
                    <div className="flex items-end gap-1 mb-5">
                      <span className="font-heading text-4xl font-black">${p.price}</span>
                      <span className="text-muted-foreground mb-1">/mo</span>
                    </div>
                    <Button
                      disabled={state === "current" || busy === p.id}
                      onClick={() => (active ? changePlan(p) : subscribe(p))}
                      data-testid={`billing-select-${p.id}`}
                      className={`w-full rounded-full font-bold ${featured ? "bg-primary text-background hover:bg-primary/90" : "bg-white/5 text-foreground border border-white/15 hover:bg-white/10"} transition-colors`}>
                      {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" />
                        : state === "current" ? "Current plan"
                        : state === "upgrade" ? <>Upgrade <ArrowUp className="w-4 h-4 ml-1" /></>
                        : state === "downgrade" ? <>Downgrade <ArrowDown className="w-4 h-4 ml-1" /></>
                        : <>Get {p.name} <Zap className="w-4 h-4 ml-1" fill="currentColor" /></>}
                    </Button>
                    <ul className="mt-6 space-y-2.5">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm">
                          <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> <span className="text-muted-foreground">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>

            {active && !pendingCancel && (
              <p className="text-xs text-muted-foreground mt-6">Plan changes are prorated automatically by Stripe — you only pay the difference.</p>
            )}
          </>
        )}
      </div>
    </DashboardShell>
  );
};

export default Billing;
